import { useCallback, useEffect, useRef, useState } from 'react';
import { PlugZap, Wallet } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { documentCaptureService } from '../services/documentCaptureService';
import { VerifierBreakerState, type VerifierStatus } from '../types/documentCapture.types';

/**
 * The second reader is down — and the scans are WAITING, not queued for a person (86d3yeh4j).
 *
 * ## Why this banner exists at all
 *
 * Before this ticket a systemic failure was invisible until it had already cost somebody an
 * afternoon: every scan that arrived during the outage filed its own review item, and the first
 * anyone knew of it was a review queue with 234 things in it that all had the same cause. Holding
 * the scans fixes the queue, but it creates a new way to be confused — rows that stop moving with no
 * explanation. This is the explanation. It is the ONLY surface that says the pipeline is paused, so
 * it has to be visible to the person watching the pipeline.
 *
 * ## Who sees it, and who can act on it
 *
 * Everyone who can open this page sees the banner; only `docscan.ai_manage` sees the button. That
 * split is deliberate: being told why your scans stopped is not an administrative privilege, but
 * asserting "it's fixed" is a claim about a shared system. Nothing is lost by not having the right —
 * the breaker probes itself every minute and recovers on its own; the button only skips the wait.
 *
 * ## Polling, not streaming
 *
 * Breaker state is not a per-file event, so it does not belong on the journey stream. A poll of a
 * two-count endpoint is cheap, and it doubles in frequency while the reader is down so the recovery
 * toast lands close to the actual recovery rather than up to half a minute after it.
 */

/** Idle cadence. Slow on purpose — while everything is fine this banner is not rendered at all. */
const POLL_IDLE_MS = 30_000;
/** Cadence while down: the operator is watching for the recovery, so we look more often. */
const POLL_DOWN_MS = 10_000;

interface SecondReaderOutageBannerProps {
  /** Whether this user may assert the reader is fixed (`docscan.ai_manage`). */
  canManageAi: boolean;
  /**
   * 86d3ygnz3 — open the second-reader settings. A budget hold is not fixed by a retry; it is fixed by
   * raising the cap, so the banner's action for that case has to land on the editor.
   */
  onOpenSettings?: () => void;
}

export function SecondReaderOutageBanner({ canManageAi, onOpenSettings }: SecondReaderOutageBannerProps) {
  const [status, setStatus] = useState<VerifierStatus | null>(null);
  const [retrying, setRetrying] = useState(false);

  // What the last poll saw, so a Down→Closed transition can be recognised. A ref, not state: it must
  // not itself cause a render, and it must survive the render that the new status triggers.
  const previous = useRef<{ down: boolean; held: number }>({ down: false, held: 0 });

  const isDown = status != null && status.state !== VerifierBreakerState.Closed;

  const apply = useCallback((next: VerifierStatus) => {
    const wasDown = previous.current.down;
    const heldBefore = previous.current.held;
    const nowDown = next.state !== VerifierBreakerState.Closed;

    // Recovery. Announced only when scans were ACTUALLY waiting — an outage that caught nothing is
    // not news, and a toast for it would train people to dismiss the ones that matter.
    if (wasDown && !nowDown && heldBefore > 0) {
      toast.success('Second reader is back', {
        description: `The ${heldBefore} scan${heldBefore === 1 ? '' : 's'} that ${
          heldBefore === 1 ? 'was' : 'were'
        } waiting ${heldBefore === 1 ? 'is' : 'are'} being checked now. None of them went to review.`,
      });
    }

    previous.current = { down: nowDown, held: nowDown ? next.heldCount : 0 };
    setStatus(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const next = await documentCaptureService.getVerifierStatus();
        if (!cancelled) apply(next);
      } catch {
        // Never break the page over a status read. A failed poll leaves the last known state on
        // screen and tries again on the next tick.
      }
      if (!cancelled) {
        timer = setTimeout(
          () => void poll(),
          previous.current.down ? POLL_DOWN_MS : POLL_IDLE_MS
        );
      }
    };

    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [apply]);

  const handleRetry = useCallback(async () => {
    // Disabled + "Retrying…" before the request, not after it — the click has to land visibly inside
    // ~100 ms and this call re-arms a backlog, so it is not instant (CLAUDE.md responsiveness gate).
    setRetrying(true);
    try {
      // The endpoint returns the POST-action status, so the banner never renders a stale "N waiting"
      // beside a button that just cleared them.
      apply(await documentCaptureService.retryVerifierNow());
    } catch {
      toast.error('Couldn’t reach the second reader just now — it will keep trying on its own.');
    } finally {
      setRetrying(false);
    }
  }, [apply]);

  // 86d3ygnz3 — the SECOND thing worth a banner: the reader is perfectly healthy and the day's budget
  // is spent, so scans are parked. Before this it rendered nothing at all, which made a cap of 0 —
  // where the release condition `used < cap` can never be true — completely silent: the pipeline stops
  // identifying anything and the only outward sign is rows that quietly stop moving.
  const capHeld =
    status != null && status.state === VerifierBreakerState.Closed && status.capHeldCount > 0;

  if (!isDown && !capHeld) return null;

  if (capHeld) return <BudgetHoldNotice held={status.capHeldCount} canManageAi={canManageAi} onOpenSettings={onOpenSettings} />;

  const held = status.heldCount;

  return (
    <div
      // role="alert" — the pipeline has paused, which is exactly the kind of thing a screen-reader
      // user must not have to discover by polling the page themselves.
      role="alert"
      className="mb-4 flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3"
    >
      <PlugZap className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 flex-1">
        {/*
          The count is stated only when there IS one. A reader can be down before it has caught
          anything — the breaker trips on the failures themselves, and until the next scan arrives
          nothing is held — which used to render "The second reader is down — 0 scans are waiting":
          an alarm that announces a problem and then reports it affected nothing. It read as a bug
          to the first person who saw it, which is how it was found.

          The banner still shows at zero, deliberately. The reader being down IS worth knowing
          BEFORE a batch goes in the folder, and the fix is the same either way. Only the tally is
          conditional — matching `apply()` above, which already suppresses the recovery toast when
          nothing was waiting, on the grounds that an outage which caught nothing is not news.
        */}
        <p className="text-sm font-medium text-foreground">
          The second reader is down
          {held > 0 && (
            <>
              {' '}
              — {held} scan{held === 1 ? '' : 's'} {held === 1 ? 'is' : 'are'} waiting
            </>
          )}
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {held > 0 ? (
            <>
              Capture is still running and nothing is lost. DOP is holding these instead of sending
              them to the review queue, because one fix clears all of them. They go through on their
              own once it’s back.
            </>
          ) : (
            <>
              Capture is still running and nothing is lost. Any scan arriving now waits here instead
              of going to the review queue, and goes through on its own once the reader is back.
            </>
          )}
        </p>
      </div>
      {canManageAi && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleRetry()}
          disabled={retrying}
          className="flex-shrink-0"
        >
          {retrying ? 'Retrying…' : 'It’s fixed — retry now'}
        </Button>
      )}
    </div>
  );
}

/**
 * 86d3ygnz3 — scans are waiting on the daily budget, not on a broken reader.
 *
 * Deliberately a different banner rather than a different sentence in the outage one: the cause, the
 * tone and above all the ACTION are different. An outage is waited out or declared fixed; a spent
 * budget is a number somebody raises. Offering "It's fixed — retry now" here would be worse than
 * useless — it re-arms the held scans, which are then refused again on the same empty budget.
 */
function BudgetHoldNotice({
  held,
  canManageAi,
  onOpenSettings,
}: {
  held: number;
  canManageAi: boolean;
  onOpenSettings?: () => void;
}) {
  return (
    <div
      role="alert"
      className="mb-4 flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3"
    >
      <Wallet className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">
          Today’s check budget is used up — {held} scan{held === 1 ? '' : 's'}{' '}
          {held === 1 ? 'is' : 'are'} waiting
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Capture is still running and nothing is lost. These scans have not gone to the review queue —
          they go through on their own at the reset (8:00 AM Manila), or as soon as the daily cap is
          raised.
        </p>
      </div>
      {canManageAi && onOpenSettings && (
        <Button size="sm" variant="outline" onClick={onOpenSettings} className="flex-shrink-0">
          Raise the cap
        </Button>
      )}
    </div>
  );
}
